import React from "react";
import { Button, Flex, Drawer, Dropdown } from "antd";
import {
  MenuOutlined,
  DownOutlined,
  PhoneOutlined,
  LineChartOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import logoImg from "../../assets/logo/dealVoice.png";

const ALL_SECTIONS = [
  { key: "home", label: "Home", sectionId: "home" },
  { key: "features", label: "Features", sectionId: "features" },
  { key: "personas", label: "Workspaces", sectionId: "personas" },
  { key: "how-it-works", label: "How It Works", sectionId: "how-it-works" },
  { key: "testimonials", label: "Testimonials", sectionId: "testimonials" },
  { key: "mobile-app", label: "Mobile App", sectionId: "mobile-app" },
  { key: "pricing", label: "Pricing", sectionId: "pricing", isPage: true },
  { key: "faq", label: "FAQ", sectionId: "faq" },
];

function Navbar({
  currentPage,
  activeSection = "home",
  setPage,
  scrollToSection,
  isNavbarScrolled,
  isMobileMenuOpen,
  toggleMobileMenu,
  openLogin,
  openSignup,
  openTrialSignup,
  isLoginOpen,
  isSignupOpen,
}) {
  const selectedKey = currentPage === "pricing" ? "pricing" : activeSection || "home";

  const handleNavClick = (key) => {
    if (key === "home") {
      setPage("home");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (key === "pricing") {
      setPage("pricing");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      scrollToSection(key);
    }
  };

  const getCurrentSectionLabel = () => {
    if (currentPage === "pricing") return "Pricing";
    const match = ALL_SECTIONS.find((s) => s.key === activeSection);
    return match ? match.label : "Home";
  };

  // Dropdown items for the compact mini scrolled navbar (hover shows all sections)
  const allSectionsDropdownItems = ALL_SECTIONS.map((sec) => ({
    key: sec.key,
    label: (
      <div className={`nav-mini-dropdown-row ${selectedKey === sec.key ? "active" : ""}`}>
        <span>{sec.label}</span>
        {selectedKey === sec.key && <span className="nav-mini-active-dot" />}
      </div>
    ),
    onClick: () => handleNavClick(sec.key),
  }));

  // Dropdown for full navbar "Features"
  const featureDropdownItems = [
    {
      key: "live-tracking",
      label: (
        <div style={{ padding: "4px 2px" }}>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>Live Call Tracking</div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Real-time call recording &amp; sync</div>
        </div>
      ),
      icon: <PhoneOutlined style={{ color: "#2563eb", fontSize: "1.1rem" }} />,
      onClick: () => handleNavClick("features"),
    },
    {
      key: "analytics",
      label: (
        <div style={{ padding: "4px 2px" }}>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>Smart Analytics</div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Conversion insights &amp; pipeline tracking</div>
        </div>
      ),
      icon: <LineChartOutlined style={{ color: "#2563eb", fontSize: "1.1rem" }} />,
      onClick: () => handleNavClick("features"),
    },
    {
      key: "communications",
      label: (
        <div style={{ padding: "4px 2px" }}>
          <div style={{ fontWeight: 600, color: "#0f172a" }}>CRM &amp; Communications</div>
          <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Seamless lead &amp; client management</div>
        </div>
      ),
      icon: <MessageOutlined style={{ color: "#2563eb", fontSize: "1.1rem" }} />,
      onClick: () => handleNavClick("features"),
    },
  ];

  return (
    <header className={`navbar ${isNavbarScrolled ? "navbar-scrolled" : ""}`}>
      {/* ── STATE 1: MINI SCROLLED NAVBAR (Compact, Right-Aligned) ── */}
      {isNavbarScrolled ? (
        <div className="nav-container nav-container-mini">
          {/* 1. Logo */}
          <button
            type="button"
            className="nav-brand-btn-mini"
            onClick={() => handleNavClick("home")}
            aria-label="DealVoice Home"
          >
            <img src={logoImg} alt="DealVoice" className="nav-logo-mini" />
          </button>

          <div className="nav-mini-divider" />

          {/* 2. Current Section with Hover Dropdown */}
          <Dropdown
            menu={{ items: allSectionsDropdownItems }}
            placement="bottomCenter"
            trigger={["hover", "click"]}
            overlayClassName="nav-mini-sections-menu"
          >
            <button type="button" className="nav-mini-section-pill">
              <span className="nav-mini-section-text">{getCurrentSectionLabel()}</span>
              <DownOutlined className="nav-mini-chevron" />
            </button>
          </Dropdown>

          {/* 3. Pricing */}
          <button
            type="button"
            className={`nav-mini-link-btn ${selectedKey === "pricing" ? "active" : ""}`}
            onClick={() => handleNavClick("pricing")}
          >
            Pricing
          </button>

          {/* 4. Sign In / Get Started */}
          <button
            type="button"
            className="nav-mini-link-btn"
            onClick={openLogin}
          >
            Sign In
          </button>

          {/* 5. Start Free Trial CTA */}
          <Button
            type="primary"
            className="nav-mini-cta-btn"
            onClick={openTrialSignup || openSignup}
          >
            Start Free Trial
          </Button>

          {/* Mobile Hamburger Button */}
          <Button
            className="hamburger-btn-mini"
            icon={<MenuOutlined />}
            type="text"
            onClick={toggleMobileMenu}
            aria-label="Open navigation menu"
          />
        </div>
      ) : (
        /* ── STATE 2: FULL TOP NAVBAR (Centered, Full Width) ── */
        <div className="nav-container">
          {/* LEFT: BRAND LOGO */}
          <div className="nav-left">
            <button
              type="button"
              className="nav-brand-btn"
              onClick={() => handleNavClick("home")}
              aria-label="DealVoice Home"
            >
              <img src={logoImg} alt="DealVoice" className="nav-logo-img" />
            </button>
          </div>

          {/* CENTER: NAV LINKS */}
          {!isLoginOpen && !isSignupOpen && (
            <div className="nav-center">
              <button
                type="button"
                className={`nav-item-link ${selectedKey === "home" ? "active" : ""}`}
                onClick={() => handleNavClick("home")}
              >
                Home
              </button>

              <Dropdown
                menu={{ items: featureDropdownItems }}
                placement="bottomCenter"
                arrow
                overlayClassName="nav-features-dropdown"
              >
                <button
                  type="button"
                  className={`nav-item-link ${selectedKey === "features" ? "active" : ""}`}
                  onClick={() => handleNavClick("features")}
                >
                  Features <DownOutlined className="nav-chevron" />
                </button>
              </Dropdown>

              <button
                type="button"
                className={`nav-item-link ${selectedKey === "pricing" ? "active" : ""}`}
                onClick={() => handleNavClick("pricing")}
              >
                Pricing
              </button>

              <button
                type="button"
                className={`nav-item-link ${selectedKey === "about" ? "active" : ""}`}
                onClick={() => handleNavClick("about")}
              >
                About
              </button>
            </div>
          )}

          {/* RIGHT: SIGN IN & PRIMARY CTA ACTION */}
          {!isLoginOpen && !isSignupOpen && (
            <div className="nav-right">
              <Flex align="center" gap={12}>
                <button
                  type="button"
                  className="nav-signin-link-btn"
                  onClick={openLogin}
                >
                  Sign In
                </button>
                <Button
                  type="primary"
                  className="nav-cta-action-btn"
                  onClick={openTrialSignup || openSignup}
                >
                  Start Free Trial
                </Button>
              </Flex>
            </div>
          )}

          {/* MOBILE HAMBURGER BUTTON */}
          <Button
            className="hamburger-btn"
            icon={<MenuOutlined />}
            type="text"
            onClick={toggleMobileMenu}
            aria-label="Open navigation menu"
          />
        </div>
      )}

      {/* MOBILE DRAWER */}
      <Drawer
        title={<img src={logoImg} alt="DealVoice" style={{ height: 30, objectFit: "contain" }} />}
        placement="right"
        onClose={toggleMobileMenu}
        open={isMobileMenuOpen}
        styles={{ body: { padding: "1.5rem" } }}
      >
        <Flex vertical gap={16}>
          {ALL_SECTIONS.map((sec) => (
            <Button
              key={sec.key}
              type={selectedKey === sec.key ? "primary" : "text"}
              block
              style={{ textAlign: "left", fontSize: 16 }}
              onClick={() => {
                handleNavClick(sec.key);
                toggleMobileMenu();
              }}
            >
              {sec.label}
            </Button>
          ))}
          <Flex vertical gap={10} style={{ marginTop: 24 }}>
            <Button
              block
              size="large"
              onClick={() => {
                toggleMobileMenu();
                openLogin();
              }}
            >
              Sign In
            </Button>
            <Button
              type="primary"
              block
              size="large"
              onClick={() => {
                toggleMobileMenu();
                if (openTrialSignup) openTrialSignup();
                else openSignup();
              }}
            >
              Start Free Trial
            </Button>
          </Flex>
        </Flex>
      </Drawer>
    </header>
  );
}

export default Navbar;
